package de.oevbs.kunde.kundenportal2.components

import de.oevbs.kosmos.ui.feedback.renderMessages
import de.oevbs.kosmos.ui.forms.checkboxgroup.standard.CheckboxGroup
import dev.fritz2.core.*
import dev.fritz2.headless.foundation.DatabindingProperty
import dev.fritz2.headless.foundation.Hook
import dev.fritz2.headless.foundation.hook
import kotlinx.coroutines.flow.map
import org.w3c.dom.HTMLLabelElement

/**
 * Kundenportal-eigene Komponente zum Rendern einer einzelnen Checkbox.
 */
class SingleCheckbox internal constructor(id: String? = null) {

    class LabelContentHook : Hook<Tag<HTMLLabelElement>, Unit, Unit>() {

        operator fun invoke(text: String) {
            value = { _, _ ->
                +text
            }
        }

        operator fun invoke(content: Tag<HTMLLabelElement>.() -> Unit) {
            value = { _, _ ->
                content()
            }
        }
    }


    /**
     * Erlaubt den Zugriff auf die ID der Komponente im Initialisierungsblock der Factory-Funktion.
     * Eine explizite Angabe durch den Benutzer ist über die Factory-Funktionen möglich. Die folgenden Prioritäten
     * gelten bei der Vergabe der ID (höchste zu niedrigste Priorität):
     *
     * Explizite ID → Store-ID des Data Bindings ([value][CheckboxGroup.value]) → Zufällig generierte ID ([Id.next])
     */
    val componentId: String by lazy { id ?: value.id ?: Id.next() }

    /**
     * Databinding, das eine externe Datenquelle zum Speichern und Validieren der Auswahl an die Checkbox-Komponente
     * koppelt.
     * Dieses wird von der Komponente zwingend vorausgesetzt. Falls kein Databinding angegeben wird, wird statt der
     * Komponente ein entsprechender Fehler gerendert.
     */
    val value: DatabindingProperty<Boolean> = DatabindingProperty()

    /**
     * Label der Checkbox.
     *
     * Erlaubt sowohl einen statischen Text als auch einen frei konfigurierbaren Content.
     */
    val label: LabelContentHook = LabelContentHook()


    fun RenderContext.render() {
        val checkedStore = storeOf(false)
        value.data handledBy checkedStore.update

        div("flex flex-col gap-3") {
            div("flex flex-row items-center gap-3") {
                input(
                    id = "single-checkbox-$componentId",
                    baseClass = "h-4 w-4 rounded"
                ) {
                    className(value.hasError.map { hasError ->
                        if (hasError) joinClasses(
                            "text-error-500 border-error-300 text-error-500 placeholder-error-300 focus:outline-none",
                            "focus:ring-error-500 focus:border-error-500"
                        )
                        else "text-primary-500 focus:ring-primary-500 focus:border-primary-500 border-gray-300"
                    })
                    type("checkbox")
                    checked(checkedStore.data)
                }.run {
                    value.handler?.invoke(this, clicks.map {
                        !checkedStore.current
                    })
                }
                label(
                    id = "single-checkbox-$componentId-label",
                    baseClass = "block text-sm font-normal text-gray-700"
                ) {
                    `for`("single-checkbox-$componentId")
                    hook(label)
                }
            }
            renderMessages(value.validationMessages)
        }
    }
}

/**
 * Rendert eine einzelne Checkbox.
 */
fun RenderContext.checkboxSingle(id: String? = null, initialize: SingleCheckbox.() -> Unit) {
    SingleCheckbox(id).run {
        initialize()
        render()
    }
}
